/**
 * #142 · Qué pestañas tiene el módulo de etiquetas, y para quién.
 *
 * Vive en un `.ts` aparte de `LabelsTabs.tsx` por la convención que se repite
 * en las cuatro capas del andamio (`useApi`, `useAuth`, `useTenantSettings`,
 * `useI18n`): **un archivo `.tsx` exporta componentes y nada más.** Un módulo
 * que exporta a la vez un componente y una función rompe el hot reload y el
 * linter lo marca.
 *
 * Rollos y Etiquetas son de todos: lo que llega físicamente al depósito y la
 * etiqueta de a una (#89). La primera no:
 *
 *   · el back-office ve **Órdenes** — la compra de XYVONA al impresor, con su
 *     proveedor y su generación de códigos;
 *   · una marca ve **Pedidos** — lo que le pide a XYVONA.
 *
 * No es una preferencia de menú. `label_order.supplier` es lo que figura en el
 * packing list del impresor, y quien le compra al impresor es XYVONA bajo su
 * propio tenant; después `backoffice_roll_assign()` mueve el rollo al cliente.
 * Mostrarle "Órdenes" a una marca le pedía inventar un proveedor que no existe
 * en su relación con nosotros, y le escondía el único camino que sí es suyo.
 *
 * Esto es ergonomía, como todo lo que se decide en el cliente, **y por ahora
 * es lo único que hay**: `POST /api/v1/label-orders` todavía le acepta la
 * orden a cualquier admin de cualquier tenant. Sostenerlo del lado del
 * servidor es #143, que se separó porque exige rehacer los 81 tests que hoy
 * crean sus órdenes como `labfarma`. Mientras tanto, no confundir esconder el
 * botón con cerrar la puerta.
 */
import type { MessageKey } from '@/i18n/messages';
import { BACKOFFICE_SLUG } from '@/layout/nav';

export type TabId = 'orders' | 'requests' | 'rolls' | 'stock' | 'units';

export interface LabelsTab {
  id: TabId;
  to: string;
  labelKey: MessageKey;
}

const ORDENES: LabelsTab = { id: 'orders', to: '/labels', labelKey: 'labels.tab.orders' };
const PEDIDOS: LabelsTab = { id: 'requests', to: '/labels', labelKey: 'labels.tab.requests' };

/**
 * La pestaña de códigos se llama «Códigos» y no «Etiquetas» (#170): la sección
 * ya se llama Etiquetas, y una pestaña con el mismo nombre que su contenedor
 * obligaba a distinguir "Etiquetas (la sección)" de "Etiquetas (la pestaña)"
 * sin ninguna pista. La ficha individual ya hablaba de "código impreso".
 *
 * Queda última a propósito: desde #159 se abre con toda la marca, pero a lo
 * que se entra con más frecuencia es a pegar un código y llegar a la ficha.
 */
const ROLLOS: LabelsTab = { id: 'rolls', to: '/labels/rolls', labelKey: 'labels.tab.rolls' };
const CODIGOS: LabelsTab = { id: 'units', to: '/labels/units', labelKey: 'labels.tab.units' };

/**
 * El stock de XYVONA —rollos comprados al proveedor y todavía sin cliente— es
 * una pestaña de ESTE módulo y no un ítem aparte del menú (#170): son los
 * mismos rollos que la pestaña Rollos, con un dueño distinto. Tenerlo bajo
 * «XYVONA › Stock de etiquetas» ponía el concepto de rollo en dos lugares del
 * menú, y quien buscaba un rollo tenía que saber primero de quién era.
 */
const STOCK: LabelsTab = { id: 'stock', to: '/labels/stock', labelKey: 'labels.tab.stock' };

/** Las pestañas de este tenant. Nunca Órdenes y Pedidos a la vez. */
export function tabsFor(tenant: string | null | undefined): readonly LabelsTab[] {
  return tenant === BACKOFFICE_SLUG
    ? [ORDENES, ROLLOS, STOCK, CODIGOS]
    : [PEDIDOS, ROLLOS, CODIGOS];
}
